/* eslint-disable max-classes-per-file */
const {XMLHttpRequest, FormData, JSON, Promise, Symbol} = globalThis;
const createErrorObject = (message, request, xhr) => ({message, request, xhr});

// https://stackoverflow.com/a/65669070/1052704
class AjaxPromise extends Promise
{
    #xhr = null;

    constructor(xhr, data, request)
    {
        super((resolve, reject) =>
        {
            xhr.onload = () =>
            {
                if (xhr.status >= 400)
                {
                    reject(createErrorObject(`${xhr.status}/${xhr.statusText}`, request, xhr));
                }
                else
                {
                    let {response} = xhr;
                    if (request._expect === 'json' && typeof response === 'string')
                    {
                        try
                        {
                            response = JSON.parse(response);
                        }
                        catch (error) {}
                    }
                    resolve(response);
                }
            };
            xhr.onerror = () => reject(createErrorObject('Request failed', request, xhr));
            xhr.onabort = () => reject(createErrorObject('Request aborted', request, xhr));
            xhr.send(data);
        });
        this.#xhr = xhr;
    }

    get xhr()
    {
        return this.#xhr;
    }

    get done()
    {
        return this.#xhr.readyState === 4;
    }

    abort()
    {
        this.#xhr.abort();
    }

    static get [Symbol.species]()
    {
        return Promise;
    }

    get [Symbol.toStringTag]()
    {
        return 'AjaxPromise';
    }
}

class AjaxRequest
{
    /**
     * Komfortabler Klassen-Wrapper um das XMLHttpRequest, der wiederverwenbar und promise-basiert ist.
     * Eine neue Instanz benutzt standardmäßig die GET-Methode und erwartet Plain-Text.
     *
     * @param {string} route Die Request-Route.
     * @see https://developer.mozilla.org/de/docs/Web/API/XMLHttpRequest
     * @example
     * import AjaxRequest from '@kids/lib/class/AjaxRequest';
     *
     * const request = new AjaxRequest('/api/user').expectJson();
     * request.post({id: 2}).then(data => console.log(data));
     * // POST-Methode und ReponseType sind jetzt im Request gespeichert, der noch einmal verwendet werden kann.
     * request.send({id: 3})
     *     .then(data => console.log(data))
     *     .catch(error => console.log(error));
     */
    constructor(route)
    {
        this._route = route;
        this._expect = 'text';
        this._method = 'GET';
        this._withCredentials = false;
        this._xhr = null;
        this._headers = {'X-Requested-With': 'XMLHttpRequest'};
    }

    /** Die Request-Route.
     * @type {string}
     */
    get route()
    {
        return this._route;
    }

    /**
     * Die letzte XMLHttpRequest Instanz vom Aufrufen von {@link AjaxRequest#post|post()},
     * {@link AjaxRequest#get|get()} oder {@link AjaxRequest#send|send()}.
     * @type {?XMLHttpRequest}
     */
    get lastXhr()
    {
        return this._xhr;
    }

    get canSendData()
    {
        const method = this._method;
        return method === 'POST' || method === 'PUT' || method === 'PATCH';
    }

    /**
     * @param {string} header
     * @param {string} value
     * @return {AjaxRequest}
     */
    addHeader(header, value)
    {
        this._headers[header] = value;
        return this;
    }

    /**
     * @return {AjaxRequest}
     */
    expectText()
    {
        this._expect = 'text';
        return this;
    }

    /**
     * @return {AjaxRequest}
     */
    expectJson()
    {
        this._expect = 'json';
        return this;
    }

    /**
     * @return {AjaxRequest}
     */
    expectDocument()
    {
        this._expect = 'document';
        return this;
    }

    /**
     * @return {AjaxRequest}
     */
    expectArrayBuffer()
    {
        this._expect = 'arraybuffer';
        return this;
    }

    /**
     * @return {AjaxRequest}
     */
    expectBlob()
    {
        this._expect = 'blob';
        return this;
    }

    /**
     * @param {boolean} [active=true]
     * @return {AjaxRequest}
     */
    withCredentials(active = true)
    {
        this._withCredentials = active;
        return this;
    }

    /**
     * Sendet einen POST-Request. Ein Plain Object wird automatisch in eine FormData-Instanz verwandelt.
     * (Nicht verschachtelt, nur: Key => String-/JSON-Repräsentation des Values.)
     * @param {*} data {@link https://developer.mozilla.org/en-US/docs/Web/API/XMLHttpRequest/send#Parameters}
     * @return {Promise}
     */
    post(data)
    {
        this._method = 'POST';
        return this.send(data);
    }

    /**
     * Sendet einen PUT-Request. Ein Plain Object wird automatisch in eine FormData-Instanz verwandelt.
     * (Nicht verschachtelt, nur: Key => String-/JSON-Repräsentation des Values.)
     * @param {*} data {@link https://developer.mozilla.org/en-US/docs/Web/API/XMLHttpRequest/send#Parameters}
     * @return {Promise}
     */
    put(data)
    {
        this._method = 'PUT';
        return this.send(data);
    }

    /**
     * Sendet einen PATCH-Request. Ein Plain Object wird automatisch in eine FormData-Instanz verwandelt.
     * (Nicht verschachtelt, nur: Key => String-/JSON-Repräsentation des Values.)
     * @param {*} data {@link https://developer.mozilla.org/en-US/docs/Web/API/XMLHttpRequest/send#Parameters}
     * @return {Promise}
     */
    patch(data)
    {
        this._method = 'PATCH';
        return this.send(data);
    }

    /**
     * Sendet einen DELETE-Request.
     * @return {Promise}
     */
    delete()
    {
        this._method = 'DELETE';
        return this.send();
    }

    /**
     * Sendet einen GET-Request.
     * @return {Promise}
     */
    get()
    {
        this._method = 'GET';
        return this.send();
    }

    /**
     * Sendet einen Request. Default mit der GET-Methode außer der letzte Request wurde mit der
     * {@link AjaxRequest#post|post()} gesendet. Ein Plain Object wird automatisch in eine FormData-Instanz
     * verwandelt. (Nicht verschachtelt, nur: Key => String-/JSON-Repräsentation des Values.)
     * @param {*} data {@link https://developer.mozilla.org/en-US/docs/Web/API/XMLHttpRequest/send#Parameters}
     * @return {Promise} Liefert ein erweitertes Promise mit einer "abort" Methode zurück
     */
    send(data)
    {
        if (!this.canSendData)
        {
            data = undefined;
        }

        if (typeof data === 'object' && !(data instanceof FormData))
        {
            const formData = new FormData();
            for (const key in data)
            {
                let value = data[key];
                const type = typeof value;

                if (type === 'function') { continue; }
                else if (type === 'object') { value = JSON.stringify(value); }
                else { value += ''; }

                formData.append(key, value);
            }
            data = formData;
        }

        this._xhr = this.createXhr();
        return new AjaxPromise(this._xhr, data, this);
    }

    /**
     * Generiert eine fertig vorkonfigurierte XMLHttpRequest Instanz.
     * @return {XMLHttpRequest}
     */
    createXhr()
    {
        const xhr = new XMLHttpRequest();

        xhr.open(this._method, this._route, true);
        xhr.responseType = this._expect;

        if (this._withCredentials)
        {
            xhr.withCredentials = true;
        }

        for (const header in this._headers)
        {
            xhr.setRequestHeader(header, this._headers[header]);
        }

        return xhr;
    }
}

export default AjaxRequest;
